// ============================================================
// Projektansichten und Beiträge mit Ort (SPEC §37) — fünf Wege für die
// Ansichten, einer für die Sprechblasen auf der Karte.
//
// Eine Ansicht ist ein gespeicherter Blick auf die Karte: die genaue Kamera
// (Mitte, Zoom, Neigung, Richtung), die Schalter der rechten Leiste und die
// Filter. Sie gehört zu EINEM Projekt (Kindtabelle `projekt_ansicht`,
// Wanderung 041) — so hat der Inhaber es am 01.10.2026 gesagt: „per project
// … we store it in project's tables“. Sehen darf sie, wer das Projekt sehen
// darf; „Nur für mich“ (`privat`) sieht nur, wer sie angelegt hat.
//
// Eine eigene Datei neben `hooks.ts` aus demselben Grund wie
// `api/freigaben.ts` und `api/startseite.ts`: `hooks.ts` ist die
// meistgeänderte Datei des Frontends. Die Regeln aus ihrem Kopf gelten hier
// genauso — Serverdaten gehören TanStack Query, Pfade ohne `/api`, nach jeder
// Änderung entwerten.
//
// ---- Die Schlüssel ----------------------------------------------------------
//
// Die Ansichten EINES Projekts liegen unter `["projekt", id, "ansichten"]` —
// bewusst UNTER dem Projekt: die Wanderung 041 hängt an `projekt_ansicht`
// einen Zeilenauslöser (`lp_notify_projekt_ansicht`), die Meldung trägt die
// Projektkennung, und `lib/ereignisse.ts` entwertet `["projekt", id]` als
// Präfix. Eine Ansicht, die jemand anderes speichert, steht so ohne Neuladen
// in der Liste. Die hausweite Reihe der Startseite und die einzelne Ansicht
// (für den Link) liegen unter `["ansichten"]`; dafür hat `lib/ereignisse.ts`
// eine eigene Zeile.
//
// Die Sprechblasen liegen unter `["aktuelles", "orte"]`: der Ereignisstrom
// entwertet bei jeder Beitragstabelle das Präfix `["aktuelles"]`, und
// `legeBeitragAb` / `useBeitragLoeschen` in `hooks.ts` entwerten den
// Schlüssel nach den EIGENEN Änderungen zusätzlich (dort ausgeschrieben,
// nicht von hier eingebunden — `hooks.ts` darf diese Datei nicht einbinden,
// sonst binden sich zwei Dateien gegenseitig ein). Die Gegenrichtung ist
// erlaubt: diese Datei liest `aktuellesSchluessel` aus `hooks.ts`.
// ============================================================
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api, frage, istApiFehler } from "./client";
import { aktuellesSchluessel } from "./hooks";
import type { AnsichtAendern, AnsichtFilter, AnsichtNeu, Beitrag, BeitragKurz, BeitragOrt, ProjektAnsicht } from "./types";
import type { Filter } from "../store/ui";

/** Die Grenze für den Namen — dieselbe Zahl wie die CHECK-Bedingung in
 *  Wanderung 041 und die Prüfung im Router. Wer sie ändert, ändert alle drei. */
export const ANSICHT_NAME_MAX = 80;

/**
 * Die Grenze aus §37.2 — darüber antwortet der Server 422. Sie gilt ZWEIMAL
 * getrennt: höchstens so viele GETEILTE Ansichten je Projekt und höchstens so
 * viele PRIVATE je Person und Projekt. Eine gemeinsame Grenze ließe ein Projekt
 * an fremden privaten Ansichten volllaufen, die außer ihren Autoren niemand
 * sieht und niemand löschen kann.
 */
export const ANSICHTEN_GRENZE = 30;

/* ========================================================== Schlüssel */

export const ansichtenSchluessel = {
  /** PRÄFIX über die hausweite Reihe und die einzelnen Ansichten — für den Ereignisstrom. */
  alle: ["ansichten"] as const,
  /** Die Ansichten EINES Projekts — unter `["projekt", id]` (siehe Kopf). */
  projekt: (id: string) => ["projekt", id, "ansichten"] as const,
  /** Die neuesten sichtbaren Ansichten im Haus (Startseite, §37.8 e). */
  neueste: (anzahl: number) => ["ansichten", "neueste", anzahl] as const,
  /** Eine Ansicht nach Kennung — für `/karte?ansicht=<id>`. */
  eine: (id: string) => ["ansichten", "eine", id] as const,
};

/** Die Sprechblasen der Karte (§37.10). Unter `["aktuelles"]`, damit der Ereignisstrom sie trifft. */
export const beitragOrteSchluessel = ["aktuelles", "orte"] as const;

/* ========================================================== Lesen */

/**
 * `GET /projekte/{id}/ansichten` — neueste zuerst; private nur die eigenen.
 * `null` fragt nicht (kein aktives und kein zuletzt aktives Projekt).
 */
export function useProjektAnsichten(projektId: string | null): UseQueryResult<ProjektAnsicht[]> {
  return useQuery({
    queryKey: ansichtenSchluessel.projekt(projektId ?? ""),
    queryFn: () => api.get<ProjektAnsicht[]>(`/projekte/${projektId}/ansichten`),
    enabled: projektId != null && projektId !== "",
    staleTime: 30_000,
  });
}

/** `GET /ansichten?anzahl=` — quer über alle Projekte, die die fragende Person sieht (Startseite). */
export function useAnsichtenNeueste(anzahl = 8): UseQueryResult<ProjektAnsicht[]> {
  return useQuery({
    queryKey: ansichtenSchluessel.neueste(anzahl),
    queryFn: () => api.get<ProjektAnsicht[]>("/ansichten" + frage({ anzahl })),
    staleTime: 60_000,
  });
}

/**
 * `GET /ansichten/{id}` — für den kopierten Link. Eine fremde private, eine
 * gelöschte oder eine Ansicht in einem unsichtbaren Projekt ist ein 404; die
 * Karte sagt dann „Diese Ansicht gibt es nicht (mehr).“ Ein 4xx wird nicht
 * wiederholt (`main.tsx`).
 */
export function useAnsicht(id: string | null): UseQueryResult<ProjektAnsicht> {
  return useQuery({
    queryKey: ansichtenSchluessel.eine(id ?? ""),
    queryFn: () => api.get<ProjektAnsicht>(`/ansichten/${id}`),
    enabled: id != null && id !== "",
    staleTime: 30_000,
  });
}

/**
 * `GET /aktuelles/orte` — jeder lebende Beitrag MIT Ort, neueste 500. `aktiv`
 * wie überall: im Schaufenster und bei ausgeschaltetem Schalter wird nicht
 * gefragt.
 */
export function useBeitragOrte(aktiv = true): UseQueryResult<BeitragOrt[]> {
  return useQuery({
    queryKey: beitragOrteSchluessel,
    queryFn: () => api.get<BeitragOrt[]>("/aktuelles/orte"),
    enabled: aktiv,
    staleTime: 60_000,
  });
}

/* ========================================================== Ändern */

/**
 * Die geladene Liste EINES Projekts sofort mit der Antwort des Servers
 * nachziehen, bevor das Entwerten neu holt. Ohne das stünde nach „Umbenennen“
 * für die Dauer einer Anfrage der alte Name in der Zeile, eine gelöschte
 * Ansicht noch da — und zweimal schnell „Ansicht speichern“ hieße zweimal
 * „<Kürzel> · Ansicht 3“: der Namensvorschlag zählt die GELADENEN Ansichten
 * (§37.7). Ist die Liste nicht geladen, bleibt es beim Entwerten (`undefined`
 * zurück heißt für TanStack: nichts schreiben).
 */
export function listeNachziehen(qc: QueryClient, projektId: string, neu: (alt: ProjektAnsicht[]) => ProjektAnsicht[]): void {
  qc.setQueryData<ProjektAnsicht[]>(ansichtenSchluessel.projekt(projektId), (alt) => (alt ? neu(alt) : alt));
}

/**
 * `POST /projekte/{id}/ansichten` → 201 `ProjektAnsicht`. Das Recht hat, wer
 * das Projekt sehen darf. Im Speichern-Dialog wechselt `projektId` mit der
 * Projektwahl — TanStack Query nimmt bei jedem Zeichnen die neue
 * `mutationFn`, gerufen wird also immer mit dem gerade gewählten Projekt.
 */
export function useAnsichtNeu(projektId: string): UseMutationResult<ProjektAnsicht, Error, AnsichtNeu> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: AnsichtNeu) => api.post<ProjektAnsicht>(`/projekte/${projektId}/ansichten`, b),
    onSuccess: (a) => {
      qc.setQueryData(ansichtenSchluessel.eine(a.id), a);
      // `a.projekt_id` und nicht `projektId`: maßgeblich ist, wohin der Server
      // geschrieben hat, nicht, was beim Zeichnen gewählt war.
      listeNachziehen(qc, a.projekt_id, (alt) => [a, ...alt.filter((x) => x.id !== a.id)]);
      void qc.invalidateQueries({ queryKey: ansichtenSchluessel.projekt(a.projekt_id) });
      void qc.invalidateQueries({ queryKey: ansichtenSchluessel.alle });
    },
  });
}

/** `PATCH /ansichten/{id} {name?, privat?}` → `ProjektAnsicht`. `privat` umschalten darf nur, wer sie angelegt hat. */
export function useAnsichtAendern(): UseMutationResult<ProjektAnsicht, Error, AnsichtAendern & { id: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: AnsichtAendern & { id: string }) => api.patch<ProjektAnsicht>(`/ansichten/${id}`, b),
    onSuccess: (a) => {
      qc.setQueryData(ansichtenSchluessel.eine(a.id), a);
      listeNachziehen(qc, a.projekt_id, (alt) => alt.map((x) => (x.id === a.id ? a : x)));
      void qc.invalidateQueries({ queryKey: ansichtenSchluessel.projekt(a.projekt_id) });
      void qc.invalidateQueries({ queryKey: ansichtenSchluessel.alle });
    },
  });
}

/**
 * `DELETE /ansichten/{id}` → 204. Hart gelöscht — eine Ansicht ist ein
 * Lesezeichen, kein Beleg. Gerufen mit der Ansicht selbst (oder Kennung und
 * Projekt), damit die richtige Projektliste entwertet wird.
 */
export function useAnsichtLoeschen(): UseMutationResult<void, Error, Pick<ProjektAnsicht, "id" | "projekt_id">> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: Pick<ProjektAnsicht, "id" | "projekt_id">) => api.del<void>(`/ansichten/${a.id}`),
    onSuccess: (_d, a) => {
      // Der Satz ist fort — ihn stehen zu lassen hieße, dass der Link aus dem
      // Zwischenspeicher noch eine gelöschte Ansicht anwendet.
      qc.removeQueries({ queryKey: ansichtenSchluessel.eine(a.id) });
      listeNachziehen(qc, a.projekt_id, (alt) => alt.filter((x) => x.id !== a.id));
      void qc.invalidateQueries({ queryKey: ansichtenSchluessel.projekt(a.projekt_id) });
      void qc.invalidateQueries({ queryKey: ansichtenSchluessel.alle });
    },
  });
}

/* ========================================================== Filter hin und zurück */

/**
 * Der Kartenfilter (`useUi.filter`) in der Form, die eine Ansicht speichert.
 *
 * MIT der Suche der Kopfleiste: sie blendet Stifte aus (`passtZuFilter`), und
 * der Inhaber will „plus filters" — ALLE Filter der Karte. Eine Ansicht
 * „Kitas Wiesbaden", gespeichert mit dem Suchwort „Kita", zeigte ohne es beim
 * Abruf jedes Projekt. Nur `personen` bleibt draußen: keine Bedienung setzt es.
 * Doppelte Einträge fallen heraus — der Server täte es sonst.
 */
export function ansichtFilterAus(f: Filter): AnsichtFilter {
  return {
    bueros: [...new Set(f.bueros)],
    phasen: [...new Set(f.phasen)],
    arten: [...new Set(f.arten)],
    teams: [...new Set(f.teams)],
    meine: f.meine,
    bald_faellig: f.baldFaellig,
    nur_oeffentlich: f.nurOeffentlich,
    suche: f.suche.trim(),
  };
}

/**
 * Der gespeicherte Filter einer Ansicht als VOLLER Kartenfilter (§37.8).
 *
 * Büros und Phasen, die es nicht mehr gibt, werden verworfen: eine Kennung,
 * die auf keinen Chip mehr passt, filterte jedes Projekt weg, und es gäbe
 * keinen Chip, an dem man das sähe — nur den Knopf „Filter zurücksetzen“.
 * Ohne `bekannt` (Stand noch nicht da) bleibt die Liste, wie sie ist.
 *
 * Die Suche kommt aus der Ansicht — auch eine LEERE ersetzt das Suchwort, das
 * gerade in der Kopfleiste steht: die Ansicht soll so aussehen, wie sie
 * gespeichert wurde, und ein stehengebliebenes Wort blendete Stifte aus, die
 * beim Speichern zu sehen waren. `personen` wird geleert (keine Bedienung).
 */
export function filterAusAnsicht(
  f: AnsichtFilter,
  bekannt?: { bueros: ReadonlySet<string>; phasen: ReadonlySet<string> },
): Filter {
  return {
    bueros: bekannt ? f.bueros.filter((id) => bekannt.bueros.has(id)) : [...f.bueros],
    phasen: bekannt ? f.phasen.filter((id) => bekannt.phasen.has(id)) : [...f.phasen],
    personen: [],
    arten: [...f.arten],
    teams: [...f.teams],
    meine: f.meine,
    baldFaellig: f.bald_faellig,
    nurOeffentlich: f.nur_oeffentlich,
    // `?? ""`: eine Ansicht, die ein älterer Server ohne `suche` lieferte,
    // leert die Suche, statt `undefined` in den Filter zu schreiben.
    suche: f.suche ?? "",
  };
}

/* ========================================================== Beitrag für die Karte */

/**
 * Ein Beitrag als Eintrag der Sprechblasen-Ebene (`BeitragOrt`), oder `null`,
 * wenn er keinen Ort trägt. Dieselben Regeln wie `GET /aktuelles/orte`: das
 * Bild ist das erste FERTIGE Bild ODER Video nach `sortierung` (§53: bei einem
 * Video sein Standbild), in der Größe „klein"; `bild_ist_video` sagt der Blase,
 * dass sie ein Play-Zeichen darüberlegt. Gleichstand in `sortierung` entscheidet
 * die Kennung des Mediums — wie `ORDER BY m.sortierung, m.id` im Router.
 * `autor_id` ist in der Kurzform `""`, wenn die Person hart gelöscht wurde
 * (`_wer` im Router) — hier wieder ehrlich `null`, wie der Vertrag sagt.
 */
export function beitragOrtAus(b: BeitragKurz): BeitragOrt | null {
  if (b.punkt == null) return null;
  const bild = b.medien
    .filter((m) => (m.art === "bild" || m.art === "video") && m.status === "fertig" && m.url_klein != null)
    .sort((x, y) => x.sortierung - y.sortierung || (x.id < y.id ? -1 : x.id > y.id ? 1 : 0))[0];
  return {
    id: b.id,
    art: b.art,
    titel: b.titel,
    punkt: b.punkt,
    projekt_id: b.projekt_id ?? null,
    autor_id: b.autor_id ? b.autor_id : null,
    angelegt_am: b.angelegt_am,
    bild_url: bild?.url_klein ?? null,
    bild_ist_video: bild?.art === "video",
  };
}

/**
 * Der Beitrag hinter `/karte?beitrag=<id>` („Auf der Karte" in Aktuelles,
 * §37.10) — als `BeitragOrt`, oder `null`, wenn es ihn nicht (mehr) gibt oder
 * er keinen Ort hat.
 *
 * Über den EINZELNEN Beitrag (`GET /aktuelles/{id}`) und nicht über die Liste
 * der Sprechblasen: die gibt es nur, solange der Schalter „Beiträge" an ist
 * (`useBeitragOrte(aktiv)` fragt sonst nicht), sie kann gerade noch laden, und
 * sie führt nur die neuesten 500. Ein Link aus Aktuelles muss in allen drei
 * Fällen gehen. Unter dem Schlüssel von `useBeitrag` — wer aus dem
 * Beitragsfenster kommt, hat ihn meist schon im Zwischenspeicher.
 *
 * Nur ein 404 heißt „nicht da". Jeder andere Fehler wird weitergeworfen:
 * „Dieser Beitrag hat keinen Ort (mehr)" nach einem Netzfehler wäre gelogen.
 */
export async function beitragFuerKarte(qc: QueryClient, id: string): Promise<BeitragOrt | null> {
  try {
    const b = await qc.fetchQuery({
      queryKey: aktuellesSchluessel.beitrag(id),
      queryFn: () => api.get<Beitrag>(`/aktuelles/${id}`),
      staleTime: 15_000,
    });
    return beitragOrtAus(b);
  } catch (e) {
    if (istApiFehler(e) && e.status === 404) return null;
    throw e;
  }
}
